import { useState, useEffect, ReactNode } from 'react';
import { Loader2, AlertCircle, GitBranch } from 'lucide-react';
import { api } from '@/api/client';

const STATE_KEY = 'gitea_oauth_state';

export function AuthGate({ children }: { children: ReactNode }) {
  const [authed, setAuthed] = useState(api.hasToken());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // When any API call receives a 401, drop back to the login screen.
  useEffect(() => {
    function handleExpired() {
      setAuthed(false);
      setError('Your session has expired. Please sign in again.');
    }
    window.addEventListener('auth:expired', handleExpired);
    return () => window.removeEventListener('auth:expired', handleExpired);
  }, []);

  // Handle the OAuth redirect back from Gitea (?code=...&state=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code  = params.get('code');
    const state = params.get('state');
    if (!code) return;

    // Verify CSRF state before doing anything else
    const savedState = sessionStorage.getItem(STATE_KEY);
    sessionStorage.removeItem(STATE_KEY);

    if (!savedState || state !== savedState) {
      setError('Security check failed — please try signing in again.');
      window.history.replaceState({}, '', '/');
      return;
    }

    // Clean the URL immediately so a reload doesn't re-submit the code
    window.history.replaceState({}, '', '/');
    setLoading(true);

    api.giteaCallback(code)
      .then(({ token }) => {
        api.setToken(token);
        setAuthed(true);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  async function handleSignIn() {
    setLoading(true);
    setError(null);
    try {
      const { url, state } = await api.giteaAuthUrl();
      sessionStorage.setItem(STATE_KEY, state);
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start sign-in');
      setLoading(false);
    }
  }

  if (authed) return <>{children}</>;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-sm w-full bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 shadow-sm">

        {/* Logo + title */}
        <div className="mb-8 text-center">
          <div className="flex justify-center mb-4">
            <img
              src="/ProEmTech.png"
              alt="ProEmTech"
              className="h-14 w-auto object-contain"
            />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">ProEmTech Console</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">KB Management Console</p>
        </div>

        {loading ? (
          <div className="flex flex-col items-center gap-3 py-4 text-gray-500 dark:text-gray-400">
            <Loader2 size={24} className="animate-spin text-brand-600" />
            <p className="text-sm">Signing in…</p>
          </div>
        ) : (
          <button
            onClick={handleSignIn}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            <GitBranch size={18} />
            Sign in with Gitea
          </button>
        )}

        {error && (
          <div className="mt-4 flex items-start gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg p-3">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

      </div>

      {/* Copyright */}
      <p className="mt-6 text-xs text-gray-400 dark:text-gray-600 text-center">
        &copy; {new Date().getFullYear()} ProEmTech&#174;. All rights reserved.
      </p>
    </div>
  );
}
